import {
  AgXToneMapping,
  Color,
  FogExp2,
  HemisphereLight,
  DirectionalLight,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  Vector2,
  WebGLRenderer,
} from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { ATMOS, GRADE, LIGHTS, SHADOWS } from './palette';
import { GradeShader } from './grade-shader';

export interface ViewportOptions {
  /** Cap device pixel ratio. Games want 1.0–1.5; 2.0 buys almost nothing here. */
  maxPixelRatio?: number;
  /** Deterministic size, used by the screenshot harness so captures are comparable. */
  fixedSize?: { width: number; height: number };
}

/**
 * Owns the WebGL context, the scene graph root, the camera, and the post chain.
 * Deliberately the only place in the codebase that touches WebGLRenderer, so the
 * headless/bot clients can run the whole game without ever constructing one.
 */
export class Viewport {
  readonly renderer: WebGLRenderer;
  readonly scene: Scene;
  readonly camera: PerspectiveCamera;
  readonly composer: EffectComposer;
  readonly bloom: UnrealBloomPass;
  readonly grade: ShaderPass;
  readonly moon: DirectionalLight;
  readonly ambient: HemisphereLight;

  private readonly maxPixelRatio: number;
  private readonly fixedSize?: { width: number; height: number };
  /** The viewmodel's camera, if one has been attached. Kept in step on resize. */
  private overlayCamera: PerspectiveCamera | null = null;

  constructor(canvasParent: HTMLElement, opts: ViewportOptions = {}) {
    this.maxPixelRatio = opts.maxPixelRatio ?? 1.5;
    this.fixedSize = opts.fixedSize;

    this.renderer = new WebGLRenderer({
      antialias: true,
      powerPreference: 'high-performance',
      stencil: false,
      // Required so the screenshot harness can read pixels back after a frame.
      preserveDrawingBuffer: true,
    });
    this.renderer.toneMapping = AgXToneMapping;
    this.renderer.toneMappingExposure = GRADE.exposure;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = PCFSoftShadowMap;
    // The moon does not move and neither does the mall, so the depth map is a
    // constant. three.js will still render it once, because `needsUpdate` is
    // armed below and again by `restampShadows()` whenever the building
    // genuinely changes shape.
    this.renderer.shadowMap.autoUpdate = false;
    this.renderer.shadowMap.needsUpdate = true;
    canvasParent.appendChild(this.renderer.domElement);

    this.scene = new Scene();
    this.scene.background = new Color(ATMOS.skyNight);
    this.scene.fog = new FogExp2(ATMOS.fogInterior.getHex(), ATMOS.density.corridor);

    this.camera = new PerspectiveCamera(72, 1, 0.05, 400);
    this.camera.position.set(0, 1.7, 0);
    // Y-up FPS camera: never roll, and yaw before pitch.
    this.camera.rotation.order = 'YXZ';

    // --- Light rig -------------------------------------------------------
    this.moon = new DirectionalLight(LIGHTS.moon.color, LIGHTS.moon.intensity);
    // Anchored over the middle of the building, not over the player. The light
    // DIRECTION is the same either way — it is the offset that defines it — so
    // nothing about how the mall is lit changes; only the shadow frustum stops
    // sliding, which is what lets the depth map be drawn once instead of 60
    // times a second. See SHADOWS.center.
    this.moon.position.set(
      SHADOWS.center[0] + LIGHTS.moon.position[0],
      LIGHTS.moon.position[1],
      SHADOWS.center[1] + LIGHTS.moon.position[2],
    );
    this.moon.castShadow = true;
    const s = this.moon.shadow;
    s.mapSize.set(SHADOWS.mapSize, SHADOWS.mapSize);
    s.bias = SHADOWS.bias;
    s.normalBias = SHADOWS.normalBias;
    const cam = s.camera;
    cam.left = -SHADOWS.cameraExtent;
    cam.right = SHADOWS.cameraExtent;
    cam.top = SHADOWS.cameraExtent;
    cam.bottom = -SHADOWS.cameraExtent;
    cam.near = 1;
    cam.far = 260;
    cam.updateProjectionMatrix();
    this.moon.target.position.set(SHADOWS.center[0], 0, SHADOWS.center[1]);
    this.scene.add(this.moon);
    this.scene.add(this.moon.target);
    this.moon.target.updateMatrixWorld();

    this.ambient = new HemisphereLight(
      LIGHTS.ambient.sky,
      LIGHTS.ambient.ground,
      LIGHTS.ambient.intensity,
    );
    this.scene.add(this.ambient);

    // --- Post chain ------------------------------------------------------
    this.composer = new EffectComposer(this.renderer);
    this.composer.addPass(new RenderPass(this.scene, this.camera));

    this.bloom = new UnrealBloomPass(
      new Vector2(1, 1),
      GRADE.bloom.strength,
      GRADE.bloom.radius,
      GRADE.bloom.threshold,
    );
    this.composer.addPass(this.bloom);

    // OutputPass applies tonemapping + sRGB; the grade must run after it.
    this.composer.addPass(new OutputPass());

    this.grade = new ShaderPass(GradeShader);
    this.grade.uniforms.uVignette.value = GRADE.vignette.amount;
    this.grade.uniforms.uVignetteSoftness.value = GRADE.vignette.softness;
    this.grade.uniforms.uGrain.value = GRADE.grain;
    this.grade.uniforms.uAberration.value = GRADE.aberration;
    this.grade.uniforms.uLift.value = [...GRADE.lift];
    this.grade.uniforms.uGain.value = [...GRADE.gain];
    this.grade.uniforms.uSaturation.value = GRADE.saturation;
    this.composer.addPass(this.grade);

    this.resize();
    if (!this.fixedSize) {
      window.addEventListener('resize', () => this.resize());
    }
  }

  /**
   * Draw a second scene on top of the world, sharing the post chain.
   *
   * For the first-person viewmodel, and only for it. Hands live in their own
   * scene with their own camera because they have to break two of the world's
   * rules at once: they are twenty centimetres from the eye, which the world's
   * 0.05 near plane clips clean through, and they must never be occluded by a
   * wall the player is standing against. A separate pass with the depth buffer
   * wiped first solves both, and costs one extra scene traversal over about
   * forty small meshes.
   *
   * It goes BEFORE bloom and the grade, not after. Hands that skip the grade
   * are a sticker on the photograph — they keep their own contrast, their own
   * saturation and no grain, and the eye finds that instantly even when it
   * cannot say why.
   */
  attachOverlay(scene: Scene, camera: PerspectiveCamera): void {
    const pass = new RenderPass(scene, camera);
    // The world has already been drawn into this buffer. Keep its colour, throw
    // away its depth, and the hands sit in front of everything.
    pass.clear = false;
    pass.clearDepth = true;
    // RenderPass does not swap, so this composites into the same target the
    // world pass wrote — insert it directly after that pass, ahead of bloom.
    this.composer.insertPass(pass, 1);
    this.overlayCamera = camera;
    this.resize();
  }

  /** Swap the atmosphere for the zone the camera currently occupies. */
  setAtmosphere(fogColor: Color, density: number) {
    const fog = this.scene.fog as FogExp2;
    fog.color.copy(fogColor);
    fog.density = density;
    (this.scene.background as Color).copy(fogColor);
  }

  /**
   * Re-photograph the building's shadow. One frame's cost, then free again.
   *
   * Call this after anything that changes what the moon can see: the mall being
   * built or rebuilt, a roof panel added, a shutter that stands outdoors. Not
   * for people walking about — they are all under a roof the moon cannot get
   * past, and the forecourt they spawn in is already inside the mall's own
   * shadow, so a moving figure has no moon shadow to lose.
   */
  restampShadows(): void {
    this.renderer.shadowMap.needsUpdate = true;
  }

  resize() {
    const w = this.fixedSize?.width ?? window.innerWidth;
    const h = this.fixedSize?.height ?? window.innerHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, this.maxPixelRatio);
    this.renderer.setPixelRatio(dpr);
    this.renderer.setSize(w, h, !this.fixedSize);
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    if (this.overlayCamera) {
      this.overlayCamera.aspect = w / h;
      this.overlayCamera.updateProjectionMatrix();
    }
  }

  render(elapsed: number) {
    this.grade.uniforms.uTime.value = elapsed;
    /**
     * Reset the counters here rather than letting three.js do it, so `info.render`
     * holds the whole frame instead of just the last pass.
     *
     * `renderer.info` auto-resets at the top of every `renderer.render()`, and the
     * composer calls that once per pass — so by the time anything reads the
     * counters they describe the final fullscreen quad and nothing else. That is
     * why the QA harness has been reporting one draw call and one triangle for a
     * building with eight thousand geometries in it, which made the draw cost of
     * this map unmeasurable exactly while it was becoming the problem.
     */
    this.renderer.info.autoReset = false;
    this.renderer.info.reset();
    this.composer.render();
  }

  dispose() {
    this.composer.dispose();
    this.renderer.dispose();
    this.renderer.domElement.remove();
  }
}
